import { useActionTrackerLoadsheetDownload } from 'apis/ActionTrackerLoadsheet/downloadActionTrackerLoadsheet'
import { useActionTrackerLoadsheetUpload } from 'apis/ActionTrackerLoadsheet/uploadActionTrackerLoadsheet'
import { useBarrierConfigAuditId } from 'apis/getBarrierConfigAuditId'
import { AxiosResponse } from 'axios'
import { ActionTrackerColumns } from 'components/ActionManagementLoadsheet/LoadsheetColumn'
import LoadsheetComponent from 'components/ActionManagementLoadsheet/LoadsheetComponent'
import BreadcrumbsComponent, { IBreadcrumbItems } from 'components/common/breadcrumbsCommon'
import BarrierConfigurationSubMenu from 'components/Header/SubMenuBarrierSubproject'
import FacilityDetailsContext from 'contexts/FacilityDetailsContext'
import { useTitle } from 'hooks/useTitle'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { LoadsheetFilters } from 'types/ActionManagementLoadsheet'
import { getKeyValueOrDefaultNested, MBtoBytes } from 'utils/utils'
import PSESubMenu from 'components/Header/SubMenuPSESubProject'
import BarrierSubMenu from 'components/Header/SubmenuProject'
import PageHeaderComponent from 'components/common/PageComponent/PageHeader'
import { Box } from '@mui/material'
import FacilityConfigContext from 'contexts/FacilityConfigContext'
import {
  AVP_SERVICE_ACTION_TRACKER,
  AVP_SERVICE_BARRIER_MANAGEMENT,
  AVP_SERVICE_PROCESS_INTEGRITY,
} from 'constants/avp_permissions'
import { useModulePermission } from 'hooks/useModulePermission'

const PK_BATCH_CREATE_UPDATE_ACTION = 'BatchCreateUpdateAssessmentFormAction'
const PK_BATCH_DOWNLOAD_ACTION = 'BatchDownloadAssessmentFormAction'

const ActionTrackerLoadsheet: React.FC = () => {
  const maxUploadSize = MBtoBytes(500, 0.5) // 500 MB
  const { asset_id: assetId, source } = useParams<{
    asset_id: string | undefined
    source: string | undefined
  }>()
  const { config } = useContext(FacilityConfigContext)
  const { checkModulesPermission, permissions} = useModulePermission()

  const isPSE = source?.toLocaleLowerCase() === 'pse'
  const isActionTracker = source?.toLocaleLowerCase() === 'action-management'

  const PermissionModule = () => {
    if (source?.toLocaleLowerCase() === 'pse') return AVP_SERVICE_PROCESS_INTEGRITY
    else if (source?.toLocaleLowerCase() === 'action-management') return AVP_SERVICE_ACTION_TRACKER
    else return AVP_SERVICE_BARRIER_MANAGEMENT
  }

  useTitle(`Action Management Loadsheet`)

  const { project_id, projectName } = useContext(FacilityDetailsContext)
  const [filters, setFilters] = useState<LoadsheetFilters>({ asset_id: '' })
  const [uploadPercentage, setUploadPercentage] = useState(0)

  const { mutateAsync: downloadLoadsheet } = useActionTrackerLoadsheetDownload()
  const { mutateAsync: uploadLoadsheet, isLoading: isUploading } = useActionTrackerLoadsheetUpload()
  const controllerRef = useRef<AbortController | null>(null)

  const { data: auditIdData } = useBarrierConfigAuditId({
    projectId: project_id,
    assetId: assetId,
    config: { cacheTime: 10000 },
  })

  const breadcrumbsItemsBarrier: IBreadcrumbItems[] = [
    { label: 'Veracity', url: process.env.REACT_APP_LOGIN_URL ?? '/' },
    { label: projectName, url: '/' },
    { label: 'OE', url: `/oe/project/${project_id}` },
    { label: 'Barrier Management', url: `/hazard-barrier-evaluation/project/${project_id}/` },
    {
      label: `${auditIdData?.asset_name ?? 'Asset'} Dashboard`,
      url: `/hazard-barrier-evaluation/project/${project_id}/asset/${assetId}`,
    },
  ]

  const breadcrumbsItemsPSE: IBreadcrumbItems[] = [
    { label: 'Veracity', url: process.env.REACT_APP_LOGIN_URL ?? '/' },
    { label: projectName, url: '/' },
    { label: 'OE', url: `/oe/project/${project_id}` },
    {
      label: getKeyValueOrDefaultNested(config, 'PSE_LABEL.TITLE_PRIMARY', 'Process Integrity'),
      url: `/process-safety-elements/project/${project_id}/`,
    },
    {
      label: `${auditIdData?.asset_name ?? ''} Dashboard`,
      url: `/process-safety-elements/project/${project_id}/asset/${assetId}/dashboard`,
    },
  ]

  const actionManagementBCItems: IBreadcrumbItems[] = [
    { label: 'Veracity', url: process.env.REACT_APP_LOGIN_URL ?? '/' },
    { label: projectName ?? '', url: '/' },
    { label: 'OE', url: `/oe/project/${project_id}` },
    { label: 'Action Management', url: `/action-management/project/${project_id}` },
  ]

  useEffect(() => {
    if (assetId) {
      setFilters((prevFilters) => ({ ...prevFilters, asset_id: assetId }))
    }
  }, [])

  const PKDownloadLoadsheet = { type: 'list' as const, modules: PermissionModule()}
  const PKUploadLoadsheet = { type: 'write' as const, modules: PermissionModule() }

  const hasDownloadPermission = useMemo(() => {
    return checkModulesPermission({ action: PK_BATCH_DOWNLOAD_ACTION, ...PKDownloadLoadsheet, assetId: filters.asset_id!,})
  }, [filters.asset_id, permissions])

  const hasUploadPermission = useMemo(() => {
    return checkModulesPermission({ action: PK_BATCH_CREATE_UPDATE_ACTION, ...PKUploadLoadsheet, assetId: filters.asset_id!,})
  }, [filters.asset_id, permissions])

  const FileDownload = async () => {
    return await downloadLoadsheet({
      project_id: project_id ?? '',
      asset_id: filters?.asset_id ?? null,
      params: {
        module_name: PermissionModule(),
      },
    })
  }

  const FileUpload = async (formdata: FormData): Promise<AxiosResponse<Blob>> => {
    setUploadPercentage(0)
    controllerRef.current = new AbortController()

    return await uploadLoadsheet({
      project_id: project_id ?? '',
      asset_id: filters?.asset_id ?? null,
      payload: formdata,
      setUploadPercentage,
      signal: controllerRef.current!.signal,
      params: {
        module_name: PermissionModule(),
      },
    })
  }

  const cancelUpload = () => {
    if (controllerRef.current) {
      controllerRef.current.abort()
      setUploadPercentage(0)
    }
  }

  const getMenuComponent = () => {
    if (isPSE) return <PSESubMenu />
    else if (isActionTracker) return <BarrierSubMenu />
    else return <BarrierConfigurationSubMenu audit_id={auditIdData?.audit_id} />
  }

  // const getMenuComponent = () => {
  //   return <BarrierConfigurationSubMenu audit_id={auditIdData?.audit_id} />
  // }

  const getBreadcrumbsItems = () => {
    if (isPSE) return breadcrumbsItemsPSE
    else if (isActionTracker) return actionManagementBCItems
    else return breadcrumbsItemsBarrier
  }

  return (
    <>
      {getMenuComponent()}
      <Box className="root-container">
        <BreadcrumbsComponent
          currentLabel="Loadsheet"
          itemList={getBreadcrumbsItems()}
          key="subproject-evaluation-page-breadcrumps"
        />

        <PageHeaderComponent header="Action Management Loadsheet" subHeader={projectName} />

        <Box className="hazard-barrier-home-title-box">
          <LoadsheetComponent
            FileDownload={FileDownload}
            FileUpload={FileUpload}
            LoadsheetColumns={ActionTrackerColumns(
              `${getKeyValueOrDefaultNested(config, 'PSE_LABEL.ABBREVIATION_PRIMARY', 'PSE')}/Barriers*`
            )}
            filters={filters}
            setFilters={setFilters}
            uploadPercentage={uploadPercentage}
            isUploading={isUploading}
            maxSize={maxUploadSize}
            cancelUpload={cancelUpload}
            disableDownload={!hasDownloadPermission}
            disableUpload={!hasUploadPermission}
          />
        </Box>
      </Box>
    </>
  )
}

export default ActionTrackerLoadsheet
